import { useEffect, useRef, useState } from "react";
import { Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { previewWorkOrderFoundationSp, pushWorkOrderToFoundationSp } from "@/lib/api";
import { BudgetBeforeCompletionDialog } from "./BudgetBeforeCompletionDialog";
import FoundationSpConfirmDialog from "./FoundationSpConfirmDialog";
import FoundationSpResultCard from "./FoundationSpResultCard";

// Work Order → Foundation via sp_insert_update_jobs (admin-only; parent
// gates visibility). Preview → confirm tax no → single call → result dialog.
export default function PushJobSpButton({ woId, lastStatus, onPushed, label = "Push to Foundation", source = "work_order", testId = "btn-foundation-push-sp-wo", className = "", autoOpen = false }) {
  const [preview, setPreview] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  // Tax number chosen in the Confirm Tax No step (null = keep resolved value).
  const [taxNo, setTaxNo] = useState(null);

  const loadPreview = async (nextTaxNo) => {
    const { data } = await previewWorkOrderFoundationSp(woId, nextTaxNo);
    setPreview(data);
    return data;
  };

  const openConfirm = async () => {
    setBusy(true);
    setTaxNo(null);
    try {
      const data = await loadPreview(null);
      if (data.blockers?.length || data.errors?.length) {
        // Blocked pushes still open the dialog so the user sees exactly why —
        // and, when the bridge can't take Phase 2 yet, the Phase 1 fallback.
        setResult({ outcome: "rejected", message: (data.blockers || data.errors).join(" "), errors: data.blockers || data.errors,
                    withheld: data.withheld, params_sent: data.to_send || data.params, required_database: data.database,
                    job_no: data.job_no, description: data.description,
                    phase1_fallback_available: data.phase1_fallback_available, bridge: data.bridge });
        return;
      }
      setConfirmOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not prepare Foundation push");
    } finally {
      setBusy(false);
    }
  };

  // Completion prompt: "Yes" mounts this button with autoOpen so the standard
  // preview → confirm flow starts immediately (once per mount).
  const autoOpened = useRef(false);
  useEffect(() => {
    if (autoOpen && !autoOpened.current) { autoOpened.current = true; openConfirm(); }
  }, [autoOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-price the preview whenever the tax no or the customer # changes so the
  // parameter list on screen is always what will actually be sent.
  const refreshPreview = async (nextTaxNo = taxNo) => {
    try { await loadPreview(nextTaxNo); } catch { /* keep the previous preview */ }
  };

  const changeTaxNo = (code) => { setTaxNo(code || null); refreshPreview(code || null); };

  const [budgetBlock, setBudgetBlock] = useState(null);

  const doPush = async (phase = "full", completionDateMode = null) => {
    setConfirmOpen(false);
    setBudgetBlock(null);
    setBusy(true);
    try {
      const { data } = await pushWorkOrderToFoundationSp(woId, source, taxNo, phase, completionDateMode);
      setResult(data);
      if (data.outcome === "success") {
        toast.success(data.completion_date_held ? `${data.message} — completion date withheld (budget not exported)` : data.message);
        window.dispatchEvent(new CustomEvent("foundation:job-pushed", { detail: { woId } }));
      } else toast.error(data.message);
      onPushed?.(data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && d?.code === "budget_not_exported") {
        setBudgetBlock(d);
        setBusy(false);
        return;
      }
      const detail = (typeof d === "string" ? d : d?.message) || "Push request failed";
      setResult({ outcome: "bridge_error", message: detail, exception_text: detail, job_no: preview?.job_no, description: preview?.description });
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button onClick={openConfirm} disabled={busy}
              className={`bg-violet-600 hover:bg-violet-700 disabled:bg-slate-300 text-white rounded-md inline-flex items-center gap-1 text-xs px-2.5 py-1.5 ${className}`}
              title={lastStatus ? `Last SP push: ${lastStatus}` : "Create/update this job in Foundation via sp_insert_update_jobs"}
              data-testid={testId}>
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
        {label}
        {lastStatus && <span className="ml-1 opacity-75">· {lastStatus}</span>}
      </button>
      <FoundationSpConfirmDialog open={confirmOpen} onOpenChange={setConfirmOpen} preview={preview}
                                 onConfirm={() => doPush("full")} testIdPrefix="wo-sp"
                                 taxNo={taxNo} onTaxNoChange={changeTaxNo}
                                 onPhase1={() => doPush("phase1")}
                                 onCustomerChanged={() => refreshPreview()} />
      <BudgetBeforeCompletionDialog block={budgetBlock} woId={woId} busy={busy}
                                    onClose={() => setBudgetBlock(null)}
                                    onPushWithout={() => doPush("full", "without")}
                                    onExported={() => doPush("full")} />
      <Dialog open={!!result} onOpenChange={(o) => { if (!o) setResult(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="wo-sp-result-dialog">
          <DialogHeader><DialogTitle>Foundation Push Result</DialogTitle></DialogHeader>
          <FoundationSpResultCard result={result} testIdPrefix="wo-sp" />
          {result && result.outcome !== "success" && (result.phase1_fallback_available || preview?.phase1_fallback_available) && (
            <div className="rounded-md border border-amber-300 bg-amber-50 p-2.5 text-xs space-y-2" data-testid="wo-sp-phase1-offer">
              <div className="font-semibold text-amber-900">Bridge upgrade still pending — push Phase 1 fields only?</div>
              <p className="text-amber-900">
                Creates the job in Foundation with just the job number and description
                (<code>{preview?.job_no}</code> — &quot;{preview?.description}&quot;). The work order stays in this queue
                flagged <strong>Phase 1 only</strong>, so you can re-push the full field set once the bridge is on v2.3.
              </p>
              <button onClick={() => { setResult(null); doPush("phase1"); }} disabled={busy}
                      className="bg-amber-600 hover:bg-amber-700 disabled:bg-slate-300 text-white rounded-md px-2.5 py-1.5 inline-flex items-center gap-1"
                      data-testid="wo-sp-phase1-push">
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                Push Phase 1 fields only
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
